/**
 * Protection Tiers settings (Spec 201)
 *
 * The protection tier IS the per-entity recovery objective — each tier defines an RPO (max
 * acceptable age of the latest backup) and an RTO (target restore time), in hours. The five tiers
 * are a fixed ladder (Starter → Platinum); edit what each one means via a modal. Settings-managed.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, Pencil } from 'lucide-react';
import { Card, CardHeader, CardBody, Button, DataTable, LoadingState, ErrorState } from '@/components/ui';
import { tiersApi, ProtectionTier } from '../../api/tiers';
import { useAuth } from '../../hooks/useAuth';
import { ProtectionTierBadge } from '../common/ProtectionTierBadge';
import { ProtectionTierModal } from './ProtectionTierModal';

const fmtDuration = (h: number | null): string => {
  if (h == null) return 'not set';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h >= 168 && h % 168 === 0) return `${h / 168} wk`;
  if (h >= 24 && h % 24 === 0) return `${h / 24} day${h / 24 === 1 ? '' : 's'}`;
  return `${h} h`;
};

export const ProtectionTiersSettings: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const tiers = useQuery({ queryKey: ['backup', 'tiers'], queryFn: tiersApi.list });
  const [editing, setEditing] = useState<ProtectionTier | null>(null);

  if (tiers.isLoading) return <LoadingState rows={5} />;
  if (tiers.isError) return <ErrorState onRetry={() => tiers.refetch()} />;

  return (
    <Card>
      <CardHeader title="Protection tiers" subtitle="Each tier is a recovery objective you assign to a source (lakehouse or warehouse)." />
      <CardBody>
        <div className="mb-4 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          <p>
            <strong>RPO</strong> (Recovery Point Objective) = the maximum acceptable age of the latest backup — how much
            data loss is tolerable. <strong>RTO</strong> (Recovery Time Objective) = how fast you must be able to restore.
            Both in hours. Assign a tier per entity on the Backups tab; the dashboard flags an entity at-risk when its last
            backup is older than its tier&apos;s RPO. Edit a tier to change what it means across every entity using it.
          </p>
        </div>
        <DataTable<ProtectionTier>
          minWidth="40rem"
          columns={[
            { key: 'tierName', header: 'Tier', width: '10rem', sortable: false, render: (t) => <ProtectionTierBadge tier={t.tierName} size="md" /> },
            { key: 'rpoHours', header: 'RPO', width: '8rem', sortable: true, render: (t) => <span className="text-[var(--color-text-primary)]">{fmtDuration(t.rpoHours)}</span> },
            { key: 'rtoHours', header: 'RTO', width: '8rem', sortable: true, render: (t) => <span className="text-[var(--color-text-primary)]">{fmtDuration(t.rtoHours)}</span> },
            { key: 'description', header: 'Description', wrap: true, sortable: false, render: (t) => <span className="text-[var(--color-text-secondary)]">{t.description ?? '—'}</span> },
            {
              key: 'actions',
              header: '',
              width: '7rem',
              align: 'right',
              sortable: false,
              render: (t) =>
                canManage ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(t)}>
                    <Pencil className="h-4 w-4" /> Edit
                  </Button>
                ) : null,
            },
          ]}
          rows={tiers.data ?? []}
          rowKey={(t) => t.tierName}
        />
      </CardBody>

      <ProtectionTierModal open={!!editing} tier={editing} onClose={() => setEditing(null)} />
    </Card>
  );
};

export default ProtectionTiersSettings;
